import React, { useEffect, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useLazyLoad } from "../hooks/useLazyLoad";
import { AdminSkill, SkillType } from "../types/admin";
import { skillsApi } from "../services/api";
import "../css/skills.css";

const SkillCard: React.FC<{ skill: AdminSkill }> = ({ skill }) => (
  <div className="skill-card">
    <i className={skill.iconClass} style={{ color: skill.iconColor }} />
    <span>{skill.name}</span>
  </div>
);

interface SkillCategoryProps {
  icon: string;
  title: string;
  skills: AdminSkill[];
}

const SkillCategory: React.FC<SkillCategoryProps> = ({ icon, title, skills }) => {
  if (skills.length === 0) return null;

  return (
    <div className="skills-category">
      <h3 className="skills-category-title">
        <i className={icon} />
        <span>{title}</span>
      </h3>
      <div className="skills-grid">
        {skills.map((skill) => (
          <SkillCard key={skill.id} skill={skill} />
        ))}
      </div>
    </div>
  );
};

const Skills: React.FC = () => {
  const { t } = useLanguage();
  const [sectionRef, isVisible] = useLazyLoad("300px");
  const [skills, setSkills] = useState<AdminSkill[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    const loadSkills = async () => {
      try {
        const response = await skillsApi.getAll({ pageSize: 100 });
        if (response.success && response.data) {
          setSkills(response.data);
        }
      } catch {
        // silently fail — section will be empty
      } finally {
        setLoaded(true);
      }
    };

    void loadSkills();
  }, [isVisible]);

  const frontEnd = skills.filter((s) => s.type === SkillType.FRONTEND);
  const backEnd = skills.filter((s) => s.type === SkillType.BACKEND);
  const devops = skills.filter((s) => s.type === SkillType.DEVOPS_TOOLS);

  return (
    <section
      className={`skills lazy-section${loaded ? " lazy-section--visible" : ""}`}
      id="skills"
      ref={sectionRef as React.RefObject<HTMLElement>}
    >
      <div className="container">
        <h2 className="section-title">{t.skillsTitle}</h2>

        <SkillCategory icon="fa-solid fa-palette" title={t.frontEnd} skills={frontEnd} />
        <SkillCategory icon="fa-solid fa-server" title={t.backEnd} skills={backEnd} />
        <SkillCategory icon="fa-solid fa-gears" title={t.devopsTools} skills={devops} />
      </div>
    </section>
  );
};

export default Skills;
